import { Dialog, DialogContent, DialogFooter } from "@/components/ui/dialog";
import { useAuth } from "@/context/auth-context";
import { useUser } from "@/context/user-context";
import { useActiveOrganization } from "@/hooks/organizations";

import { useCreateWorkspace } from "@/queries";
import { displayErrorToast } from "@/utils/toast";
import { LockClosedIcon } from "@heroicons/react/outline";
import { FC, FormEvent, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

type WorkspaceModalProps = {
  setOpen: (isOpen: boolean) => void;
};

const WorkspaceModal: FC<WorkspaceModalProps> = ({ setOpen }) => {
  const [name, setName] = useState("");
  const authContext = useAuth();
  const userToken = authContext!.userToken;
  const createWorkspace = useCreateWorkspace(userToken!);
  const navigate = useNavigate();
  const userContext = useUser();
  const activeOrganization = useActiveOrganization();

  const handleClose = () => {
    setOpen(false);
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!activeOrganization?.id) {
      displayErrorToast("Organization is required to create a workspace.");
      return;
    }
    try {
      const workspace = await createWorkspace.mutateAsync({
        name,
        organization_id: activeOrganization.id,
      });
      userContext!.setWorkspaceId(workspace.id);
      navigate(`/workspace/${workspace.id}/settings`);
      setOpen(false);
    } catch (error: unknown) {
      console.log(error);
    }
  };

  const renderContent = () => {
    if (!userContext || userContext.userIsFreePlan)
      return (
        <div className="space-y-6">
          <div className="border-l border-gray-400 p-4">
            <p className="pb-2 text-lg font-semibold">
              <LockClosedIcon className="mr-1 inline-block h-4 w-4" />
              Upgrade Required
            </p>
            <p className="text-md">
              You must upgrade to a pro plan to create new workspaces. Upgrade
              by clicking "Billing" in the settings cog on the top right corner.
            </p>
          </div>
          <DialogFooter className="flex gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              className="flex-1"
            >
              Close
            </Button>
          </DialogFooter>
        </div>
      );

    return (
      <div className="space-y-6">
        <div className="space-y-2">
          <h3 className="text-lg font-semibold text-gray-900">
            Create Workspace
          </h3>
          <p className="text-sm text-gray-600">
            Create a new workspace to organize your projects.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              Workspace name
            </label>
            <Input
              id="workspace_name"
              name="workspace_name"
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. My Workspace"
              required
              type="text"
              value={name}
              className="h-10"
            />
          </div>
          <DialogFooter className="flex gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex-1"
              disabled={createWorkspace.isLoading}
            >
              {createWorkspace.isLoading ? "Creating..." : "Create"}
            </Button>
          </DialogFooter>
        </form>
      </div>
    );
  };

  return (
    <Dialog open={true} onOpenChange={handleClose}>
      <DialogContent>{renderContent()}</DialogContent>
    </Dialog>
  );
};

export default WorkspaceModal;
